/**
 * CustomersTab - Customer list tab content
 *
 * Displays a searchable, paginated table of all customers.
 * Uses the same "with-dropdown" SearchBar pattern as the Consent Management page
 * to support multi-domain search (Consent ID, Mobile Number, + dynamic csv_meta fields).
 */

import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalPopover } from '../../../hooks/usePortalPopover';
import { Sparkles, SlidersHorizontal, Filter } from 'lucide-react';
import { SearchBar, Loader } from 'equal-ds-ui';
import { CustomersTabProps, BalanceFilter, BalanceFilterOperator, formatINR, SearchDomain } from '../../../types/ews.types';
import { EWSCustomersTable } from './EWSCustomersTable';

/** Convert a normalized snake_case meta key to a readable label. e.g. "customer_name" → "Customer Name" */
const metaKeyToLabel = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const BALANCE_FILTER_DROPDOWN_Z = 99999;

const OPERATORS: { value: BalanceFilterOperator; label: string }[] = [
  { value: '>', label: 'Greater than (>)' },
  { value: '<', label: 'Less than (<)' },
  { value: '=', label: 'Equal to (=)' },
  { value: '>=', label: 'Greater than or equal (>=)' },
  { value: '<=', label: 'Less than or equal (<=)' },
];

const BalanceFilterBar: React.FC<{
  balanceFilter: BalanceFilter | null;
  onApply: (filter: BalanceFilter | null) => void;
  isLoading: boolean;
}> = ({ balanceFilter, onApply, isLoading }) => {
  const { open, pos, triggerRef, panelRef, toggle, close } = usePortalPopover({ closeOnScroll: true });
  const [operator, setOperator] = useState<BalanceFilterOperator>(balanceFilter?.operator ?? '>');
  const [value, setValue] = useState<string>(balanceFilter?.value?.toString() ?? '');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    setOperator(balanceFilter?.operator ?? '>');
    setValue(balanceFilter?.value?.toString() ?? '');
  }, [balanceFilter]);

  // Equal DS applies `padding: 0 !important` to buttons inside equal-ds-root,
  // which overrides React inline styles. Force-apply via setProperty.
  useLayoutEffect(() => {
    triggerRef.current?.style.setProperty('padding', '8px 16px', 'important');
  });

  const handleApply = () => {
    const num = parseFloat(value);
    // Reject NaN and negative balances. min=0 on the <input> is enforced by the
    // spinner UI only — users can still type "-100" directly, so guard here too.
    if (isNaN(num) || num < 0) return;
    onApply({ operator, value: num });
    close();
  };

  const handleClear = () => {
    setValue('');
    setOperator('>');
    onApply(null);
    close();
  };

  const triggerLabel = balanceFilter
    ? `${balanceFilter.operator} ${formatINR(balanceFilter.value)}`
    : 'Select Balance';

  // Mirror the handleApply guard so the Apply button disables on negative input.
  const isInvalid = (() => {
    const n = parseFloat(value);
    return isNaN(n) || n < 0;
  })();

  return (
    <div style={{ minWidth: '200px' }}>
      <label
        className="block text-xs font-normal text-text-tertiary tracking-wide mb-1"
        style={{ textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.1em' }}
      >
        Current Balance
      </label>
      <button
        ref={triggerRef}
        onClick={toggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          width: '100%',
          transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
          transition: 'all 0.2s ease-in-out',
          backgroundColor: isHovered
            ? 'var(--color-background-primary)'
            : 'var(--color-background-secondary)',
          color: 'var(--color-text-primary)',
          borderColor: isHovered
            ? 'var(--color-border-hover)'
            : 'var(--color-border-default)',
          fontSize: 'var(--typography-fontSize-sm, 14px)',
          fontWeight: 'var(--typography-fontWeight-medium, 500)',
          letterSpacing: '0.05em',
          padding: '12px 20px',
          borderRadius: '6px',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderBottomWidth: '2px',
          boxShadow: isHovered ? 'var(--shadow-md, 0 4px 6px rgba(0,0,0,0.07))' : 'var(--core-shadows-sm, 0 1px 2px rgba(0,0,0,0.05))',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {isLoading && balanceFilter ? (
          <svg className="animate-spin flex-shrink-0" style={{ width: 16, height: 16 }} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#d1e8f0" strokeWidth="3" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="#1a7090" strokeWidth="3" strokeLinecap="round" />
          </svg>
        ) : (
          <SlidersHorizontal style={{ width: 16, height: 16, flexShrink: 0, color: balanceFilter ? '#1a7090' : 'inherit' }} />
        )}
        <span style={{ flex: 1, textAlign: 'left', color: balanceFilter ? '#1a7090' : 'var(--color-text-secondary, #6b7280)' }}>
          {triggerLabel}
        </span>
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            zIndex: BALANCE_FILTER_DROPDOWN_Z,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: '280px',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ fontSize: '14px', fontWeight: 600, color: '#111827', margin: 0 }}>Filter by Balance</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>Operator</label>
              <select
                value={operator}
                onChange={e => setOperator(e.target.value as BalanceFilterOperator)}
                style={{
                  width: '100%', padding: '8px 12px', fontSize: '14px',
                  border: '1px solid #d1d5db', borderRadius: '8px',
                  background: '#f9fafb', color: '#111827',
                }}
              >
                {OPERATORS.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>Amount (₹)</label>
              <input
                type="number"
                min="0"
                value={value}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleApply(); }}
                placeholder="e.g. 50000"
                style={{
                  width: '100%', padding: '8px 12px', fontSize: '14px',
                  border: '1px solid #d1d5db', borderRadius: '8px',
                  background: '#fff', color: '#111827',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
              <button
                onClick={handleApply}
                disabled={isInvalid}
                style={{
                  flex: 1, padding: '8px', fontSize: '14px', fontWeight: 600,
                  borderRadius: '8px', cursor: isInvalid ? 'not-allowed' : 'pointer',
                  background: isInvalid ? '#e5e7eb' : 'linear-gradient(to bottom, #F8FEFF, #F0F9FF)',
                  color: isInvalid ? '#9ca3af' : '#0F3340',
                  border: isInvalid ? 'none' : '1px solid #d1e8f0',
                  opacity: isInvalid ? 0.5 : 1,
                }}
              >
                Apply
              </button>
              <button
                onClick={handleClear}
                style={{
                  flex: 1, padding: '8px', fontSize: '14px', fontWeight: 600,
                  borderRadius: '8px', border: '1px solid #d1d5db',
                  background: '#f9fafb', color: '#374151', cursor: 'pointer',
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export const CustomersTab: React.FC<CustomersTabProps> = ({
  onPageChange,
  onPageSizeChange,
  searchQuery,
  searchDomain,
  onSearch,
  customers,
  pagination,
  isLoading,
  onCustomerClick,
  sortBy,
  sortOrder,
  onSortChange,
  enabledMetaColumns,
  metaColumns,
  balanceFilter,
  onBalanceFilterChange,
  riskLevelFilter,
  onRiskLevelFilterChange,
}) => {
  const [localQuery, setLocalQuery] = React.useState(searchQuery);
  const [localDomain, setLocalDomain] = React.useState<string>(searchDomain);

  // Sync local state with props
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    setLocalDomain(searchDomain);
  }, [searchDomain]);

  // Debounced search effect
  useEffect(() => {
    const handler = window.setTimeout(() => {
      onSearch(localQuery, localDomain as SearchDomain);
    }, 400);
    return () => window.clearTimeout(handler);
  }, [localQuery, localDomain, onSearch]);

  // Build search domains: fixed + dynamic from metaColumns
  const searchDomains = useMemo(() => {
    const fixed = [
      { value: 'consent_id', label: 'Consent ID' },
      { value: 'mobile_number', label: 'Mobile Number' },
    ];
    if (!metaColumns || metaColumns.length === 0) return fixed;
    const dynamic = metaColumns.map((col) => ({
      value: `meta_${col}`,
      label: metaKeyToLabel(col),
    }));
    return [...fixed, ...dynamic];
  }, [metaColumns]);

  const hasDropdown = searchDomains.length > 2; // More than just the 2 fixed domains

  // Placeholder based on selected domain
  const placeholder = useMemo(() => {
    const domain = searchDomains.find((d) => d.value === localDomain);
    return domain ? `Search by ${domain.label}...` : 'Search...';
  }, [localDomain, searchDomains]);

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-lg bg-white">
      {/* Header with Search and Filter */}
      <div className="flex flex-col items-start space-y-4 py-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-500" />
          <h3 className="text-lg font-semibold text-gray-900">All Customers</h3>
        </div>
        <div className={hasDropdown ? 'w-[480px]' : 'w-80'}>
          <SearchBar
            variant={hasDropdown ? 'with-dropdown' : 'simple'}
            domains={hasDropdown ? searchDomains : undefined}
            selectedDomain={hasDropdown ? localDomain : undefined}
            onDomainChange={hasDropdown ? (value: string) => setLocalDomain(value) : undefined}
            value={localQuery}
            onChange={setLocalQuery}
            placeholder={placeholder}
          />
        </div>
        <div className="w-full bg-white border border-border-default rounded-xl p-4 shadow-md flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-background-tertiary p-1.5 rounded-lg">
              <Filter className="w-4 h-4 text-text-primary" />
            </div>
            <span className="text-xl font-medium text-text-primary tracking-wider">Filter By</span>
          </div>
          <div className="flex flex-wrap gap-4">
            <BalanceFilterBar
              balanceFilter={balanceFilter}
              onApply={onBalanceFilterChange}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Customer Table */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {customers && pagination ? (
          <EWSCustomersTable
            customers={customers}
            isLoading={isLoading}
            pagination={pagination}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClick={onCustomerClick}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
            enabledMetaColumns={enabledMetaColumns}
            riskLevelFilter={riskLevelFilter}
            onRiskLevelFilterChange={onRiskLevelFilterChange}
          />
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader size={72} cellSize={20} />
          </div>
        ) : (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">No customers found</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersTab;
